import { useEffect, useState } from 'react';
import { FiSmartphone, FiTrash2 } from 'react-icons/fi';

import { ConfirmationModal, Modal } from '@/components';
import { ButtonOutlinedSmall } from '@/components/Button';
import { Switch } from '@/components/Inputs';
import { getDeviceInfo } from '@/store/notification-settings/device-info';
import {
  useDeleteNotificationMethodMutation,
  useUpdateNotificationMethodMutation,
} from '@/store/notification-settings/notification-settings.store';
import type { NotificationMethod } from '@/store/notification-settings/notification-settings.types';

import s from '../NotificationsSettingsPage.module.scss';

type Props = {
  onClose: () => void;
  devices: NotificationMethod[];
};

export const ManagePushDevicesModal = ({ onClose, devices }: Props) => {
  const { mutate: updateMethod } = useUpdateNotificationMethodMutation();
  const { mutate: deleteMethod } = useDeleteNotificationMethodMutation();
  const [pendingDelete, setPendingDelete] = useState<NotificationMethod | null>(null);
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null);

  useEffect(() => {
    getDeviceInfo()
      .then(({ deviceId }) => setCurrentDeviceId(deviceId))
      .catch(() => setCurrentDeviceId(null));
  }, []);

  const isCurrentDevice = (m: NotificationMethod) => !!m.deviceId && m.deviceId === currentDeviceId;
  const deviceLabel = (m: NotificationMethod) => m.deviceName || 'Unknown device';

  return (
    <>
      <Modal open onClose={onClose} title="Manage Push Devices">
        <div className={s.modalBody}>
          <div className={s.group}>
            {devices.length === 0 && (
              <div className={s.empty}>
                Please allow notifications in the VesselHaven mobile app to add a device.
              </div>
            )}
            {devices.map((m) => (
              <div key={m.id} className={s.row}>
                <div className={s.rowIcon}>
                  <FiSmartphone />
                </div>
                <div className={s.rowText}>
                  <div className={s.rowTitle}>
                    {deviceLabel(m)}
                    {isCurrentDevice(m) && <span className={s.currentTag}>(current)</span>}
                  </div>
                  <div className={s.rowSub}>{m.active ? 'Active' : 'Inactive'}</div>
                </div>
                <div className={s.deviceActions}>
                  <Switch
                    checked={m.active}
                    onChange={() =>
                      updateMethod({ id: m.id, type: m.type, data: m.data, active: !m.active })
                    }
                  />
                  <button
                    type="button"
                    className={s.deleteBtn}
                    onClick={() => setPendingDelete(m)}
                    title="Remove Device"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className={s.modalActions}>
            <ButtonOutlinedSmall onClick={onClose}>Close</ButtonOutlinedSmall>
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) deleteMethod({ id: pendingDelete.id });
        }}
        title="Remove this device?"
        message={
          pendingDelete
            ? `Stop sending push notifications to “${deviceLabel(pendingDelete)}”? You’ll need to register this device again to receive them.`
            : ''
        }
        confirmText="Remove"
      />
    </>
  );
};
